class HowToUse extends HTMLElement {
  constructor() {
    super();
    this.steps = Array.from(this.querySelectorAll('.how-to-use__step'));
    this.imageWrappers = Array.from(this.querySelectorAll('.how-to-use__image-wrapper'));
    this.stepsContainer = this.querySelector('.how-to-use__steps');
    this.activeIndex = 0;

    this.naturalHeights = [];
    this.onResize = this.measureNaturalHeights.bind(this);
    this.mediaContainer = this.querySelector('.how-to-use__media');
    this.touchStartY = null;
    this.touchStartX = null;
    this.touchDirection = null;
    this.touchCaptured = false;
    this.isDragging = false;
    this._animationTimer = null;
  }

  connectedCallback() {
    if (!this.steps.length) return;

    this.steps.forEach((step, i) => {
      step.setAttribute('role', 'button');
      step.setAttribute('tabindex', '0');
      step.addEventListener('click', () => this.setActive(i));
      step.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.setActive(i);
        }
      });
    });

    const touchTargets = [this.stepsContainer, this.mediaContainer].filter(Boolean);
    const onStart = this.onTouchStart.bind(this);
    const onMove = this.onTouchMove.bind(this);
    const onEnd = this.onTouchEnd.bind(this);
    touchTargets.forEach((target) => {
      target.addEventListener('touchstart', onStart, { passive: true });
      target.addEventListener('touchmove', onMove, { passive: false });
      target.addEventListener('touchend', onEnd, { passive: true });
      target.addEventListener('touchcancel', onEnd, { passive: true });
    });

    this.measureNaturalHeights();

    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  measureNaturalHeights() {
    this.steps.forEach((step) => {
      step.style.setProperty('--step-natural-h', '100rem');
      step.classList.remove('is-hidden');
    });
    this.naturalHeights = this.steps.map((step) => step.offsetHeight);
    this.steps.forEach((step, i) => {
      step.style.setProperty('--step-natural-h', `${this.naturalHeights[i]}px`);
    });
    this.setActive(this.activeIndex);
  }

  isMobile() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  setActive(index) {
    const total = this.steps.length;
    const clamped = Math.max(0, Math.min(total - 1, index));
    this.activeIndex = clamped;
    const mobile = this.isMobile();

    this.steps.forEach((step, i) => {
      const isActive = i === clamped;
      const linearDepth = i - clamped;
      let depth;
      let isHidden;

      if (mobile) {
        // Cyclic stack: active=0, future steps=1..N-1, past steps wrap to the
        // back so the just-swiped step appears at the bottom of the stack.
        depth = (i - clamped + total) % total;
        isHidden = false;
      } else {
        isHidden = false;
        depth = Math.max(0, linearDepth);
      }

      step.style.setProperty('--step-depth', depth);
      step.style.setProperty('--step-order', depth);
      step.classList.toggle('is-active', isActive);
      step.classList.toggle('is-hidden', isHidden);
      step.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      step.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
    });

    this.imageWrappers.forEach((wrapper, i) => {
      wrapper.classList.toggle('is-active', i === clamped);
      wrapper.classList.toggle('is-above', i < clamped);
    });
  }

  onTouchStart(event) {
    if (!this.isMobile()) return;
    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchDirection = null;
    this.touchCaptured = false;
    this.isDragging = false;
  }

  onTouchMove(event) {
    if (this.touchStartY === null) return;
    const touch = event.touches[0];
    const dx = touch.clientX - this.touchStartX;
    const dy = touch.clientY - this.touchStartY;

    // detect direction
    if (!this.touchDirection) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      this.touchDirection =
          Math.abs(dy) > Math.abs(dx)
              ? 'vertical'
              : 'horizontal';
    }
    // allow normal page scroll
    if (this.touchDirection !== 'vertical') return;
    // only trigger swipe when movement is large enough
    if (Math.abs(dy) < 30) return;
    // prevent browser scroll ONLY during active swipe
    event.preventDefault();
    this.touchCaptured = false;
    this.isDragging = false;
    this.classList.add('is-animating');
    this.applyDrag(dy);
  }

  onTouchEnd(event) {
    if (this.touchStartY === null) return;
    const touch = event.changedTouches && event.changedTouches[0];
    const dy = touch ? touch.clientY - this.touchStartY : 0;
    const captured = this.touchCaptured;
    const wasDragging = this.isDragging;

    this.touchStartX = null;
    this.touchStartY = null;
    this.touchDirection = null;
    this.touchCaptured = false;
    this.isDragging = false;

    if (!captured || Math.abs(dy) < 50) {
      if (wasDragging) this.snapBack();
      return;
    }

    const direction = dy < 0 ? 1 : -1;
    const total = this.steps.length;
    let next = this.activeIndex + direction;
    if (this.isMobile()) {
      // Wrap around so the carousel loops on mobile.
      next = (next + total) % total;
    } else if (next < 0 || next >= total) {
      this.snapBack();
      return;
    }
    this.commitDrag(next);
  }

  applyDrag(dy) {
    const containerHeight = (this.mediaContainer && this.mediaContainer.offsetHeight) || 1;
    const damped = dy * 0.5;
    const activeWrapper = this.imageWrappers[this.activeIndex];
    if (activeWrapper) {
      activeWrapper.style.transition = 'none';
      activeWrapper.style.transform = `translateY(${damped}px)`;
      activeWrapper.style.opacity = '1';
    }

    const targetIdx = dy < 0 ? this.activeIndex + 1 : this.activeIndex - 1;
    if (targetIdx < 0 || targetIdx >= this.imageWrappers.length) return;

    const target = this.imageWrappers[targetIdx];
    if (!target) return;
    const startOffset = dy < 0 ? containerHeight : -containerHeight;
    target.style.transition = 'none';
    target.style.transform = `translateY(${startOffset + damped}px)`;
    target.style.opacity = '1';
  }

  snapBack() {
    const transition = 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease';
    this.imageWrappers.forEach((wrapper, i) => {
      wrapper.style.transition = transition;
      wrapper.style.transform = this.getRestingTransform(i, this.activeIndex);
      wrapper.style.opacity = i === this.activeIndex ? '1' : '0';
    });
    this.clearInlineStylesAfter(250);
  }

  commitDrag(nextIndex) {
    const transition = 'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.45s ease';
    this.imageWrappers.forEach((wrapper, i) => {
      wrapper.style.transition = transition;
      wrapper.style.transform = this.getRestingTransform(i, nextIndex);
      wrapper.style.opacity = i === nextIndex ? '1' : '0';
    });
    this.setActive(nextIndex);
    this.clearInlineStylesAfter(350);
  }

  clearInlineStylesAfter(ms) {
    window.clearTimeout(this._animationTimer);
    this._animationTimer = window.setTimeout(() => {
      this.imageWrappers.forEach((wrapper) => {
        wrapper.style.transition = '';
        wrapper.style.transform = '';
        wrapper.style.opacity = '';
      });
      this.classList.remove('is-animating');
    }, ms);
  }

  getRestingTransform(index, activeIndex) {
    if (index === activeIndex) return 'translateY(0)';
    if (index < activeIndex) return 'translateY(-100%)';
    return 'translateY(100%)';
  }
}

customElements.define('how-to-use', HowToUse);
